"use client"

import { resolveApp } from "@/i18n/apps"
import { getUserCountryOriginClient } from "@/lib/cookie/site-cookie/cookieActions.client"
import { UNIVERSAL_DEFAULT_LANGUAGE } from "@/lib/siteconfig/constants"
import { usePathname } from "next/navigation"
import { useMemo } from "react"

export interface SiteContext {
    site_country: string
    site_language: string
}

/**
 * Extracts locale from pathname (e.g., "/en/product" -> "en")
 */
export const getLocaleFromPathname = (pathname: string): string => {
    if (!pathname) {
        return UNIVERSAL_DEFAULT_LANGUAGE
    }

    const segments = pathname.split("/").filter(Boolean)
    if (segments.length === 0) {
        return UNIVERSAL_DEFAULT_LANGUAGE
    }

    const locale = segments[0]

    if (!locale || locale.length < 2) {
        return UNIVERSAL_DEFAULT_LANGUAGE
    }

    return locale
}

/**
 * Provides tracking context (`site_country`, `site_language`) for analytics events.
 *
 * ### `site_language`
 * Always derived from the URL locale via `resolveApp`. `/en*` → "EN", `/de*` → "DE",
 * `/fr*` → "FR". URL is the authoritative source; no basket is needed.
 *
 * ### `site_country`
 * Primary source is the geo-IP country from the proxy-set `user_country_origin` cookie.
 * When the cookie is missing or empty, we fall back to the URL-encoded country
 * (`app.countryCode`, non-null only on locale-specific pages like `/en-se`, `/de-ch`),
 * and finally to `""`. The basket/session country is not consulted.
 *
 * We deliberately do NOT add a hardcoded terminal country default: an unknown-geo visitor
 * on a language-only locale (`/en`, `/de`) is genuinely country-less, and stamping a fixed
 * country would fabricate data and pollute GA4's country dimension — the same reason the
 * proxy no longer defaults the geo header. `app.countryCode` is safe as a fallback because
 * it is real, URL-derived truth; a constant would not be. Empty `""` lets those events
 * gate out honestly rather than be mis-attributed.
 *
 * The result is memoized to a stable reference so consumers that pass it into effect /
 * callback dependency arrays don't re-fire every render.
 */
export const useSiteContext = (): SiteContext => {
    const pathname = usePathname()
    const app = resolveApp(getLocaleFromPathname(pathname))

    // Geo-IP country from the proxy-set `user_country_origin` cookie — the client-side
    // surface of the geo-IP header (already uppercased, validated to a real shipping
    // country at write time). Read directly in render: it's a cheap cookie parse and the
    // value only feeds analytics events (effects), never rendered DOM, so there is no
    // hydration/flicker concern that would warrant useState/useEffect.
    //
    // `||` (not `??`) so an empty-string cookie value also falls through to app.countryCode.
    const site_country = getUserCountryOriginClient() || app.countryCode || ""
    const site_language = app.language.toUpperCase()

    return useMemo(() => ({ site_country, site_language }), [site_country, site_language])
}
